import {
  Delete as DeleteIcon,
  Edit,
  Refresh,
  Visibility,
  ZoomIn,
  ZoomOut,
  PanTool
} from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Toolbar,
  Tooltip,
  Typography
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../../../core-components/theme/components/Button/Button';
import { AnnotatedSection, TableData, useOCRStore } from '../store/ocrStore';

interface CanvasComponentProps {
  imageUrl: string;
  isMainCanvas?: boolean;
  annotationData?: AnnotatedSection;
  onAnnotationCreate?: (annotation: AnnotatedSection) => void;
  onDelete?: () => void;
  title?: string;
  tableData?: TableData | null;
}

interface CanvasState {
  isAnnotating: boolean;
  isPanning: boolean;
  zoom: number;
  isDrawing: boolean;
  isDragging: boolean;
  startPoint: { x: number; y: number } | null;
  panOffset: { x: number; y: number };
}

interface ImageTransform {
  offsetX: number;
  offsetY: number;
  displayWidth: number;
  displayHeight: number;
  scale: number;
}

export const CanvasComponent: React.FC<CanvasComponentProps> = ({
  imageUrl,
  isMainCanvas = true,
  annotationData,
  title,
  tableData,
  onAnnotationCreate,
  onDelete
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const baseScaleRef = useRef<number>(1);
  const transformRef = useRef<ImageTransform>({ offsetX: 0, offsetY: 0, displayWidth: 0, displayHeight: 0, scale: 1 });
  const selectedBlockId = useOCRStore((state) => state.selectedBlockId);
  const annotationIds = useOCRStore((state) => state.annotationIds);
  const toggleAllCoverage = useOCRStore((state) => state.toggleAllCoverage);
  const showCoverage = useOCRStore((state) => state.showCoverage);
  const isAllCoverageOn = annotationIds.length > 0 && annotationIds.every(id => showCoverage.has(id));

  const [canvasState, setCanvasState] = useState<CanvasState>({
    isAnnotating: false,
    isPanning: false,
    zoom: 1,
    isDrawing: false,
    isDragging: false,
    startPoint: null,
    panOffset: { x: 0, y: 0 }
  });
  
  const [currentSelection, setCurrentSelection] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || !imageUrl) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Enable high-quality rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageRef.current = img;
      
      // Set canvas size to container size for better quality
      const containerRect = container.getBoundingClientRect();
      const canvasWidth = Math.floor(containerRect.width);
      const canvasHeight = isMainCanvas ? 400 : 250; // Increased height for better quality
      
      // Set actual canvas resolution higher for crisp rendering
      const dpr = window.devicePixelRatio || 1;
      canvas.width = canvasWidth * dpr;
      canvas.height = canvasHeight * dpr;
      canvas.style.width = `${canvasWidth}px`;
      canvas.style.height = `${canvasHeight}px`;
      
      // Scale the drawing context to match device pixel ratio
      ctx.scale(dpr, dpr);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      // Calculate base scale to fit image in container
      const padding = 20;
      const fitWidth = canvasWidth - 2 * padding;
      const fitHeight = canvasHeight - 2 * padding;
      
      const imageAspectRatio = img.width / img.height;
      const fitAspectRatio = fitWidth / fitHeight;
      
      let baseScale;
      if (imageAspectRatio > fitAspectRatio) {
        baseScale = fitWidth / img.width;
      } else {
        baseScale = fitHeight / img.height;
      }
      
      // Don't scale down too much for quality
      baseScale = Math.min(baseScale, 1);
      baseScaleRef.current = baseScale;
      
      // Reset pan offset when image loads
      setCanvasState(prev => ({ ...prev, panOffset: { x: 0, y: 0 } }));
      
      drawCanvas();
    };
    img.src = imageUrl;
  }, [imageUrl, isMainCanvas]);

  const calculateImageTransform = useCallback((): ImageTransform => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return { offsetX: 0, offsetY: 0, displayWidth: 0, displayHeight: 0, scale: 1 };

    const currentScale = baseScaleRef.current * canvasState.zoom;
    const displayWidth = img.width * currentScale;
    const displayHeight = img.height * currentScale;
    
    // Apply pan offset
    const baseOffsetX = (canvas.width / (window.devicePixelRatio || 1) - displayWidth) / 2;
    const baseOffsetY = (canvas.height / (window.devicePixelRatio || 1) - displayHeight) / 2;
    
    const offsetX = baseOffsetX + canvasState.panOffset.x;
    const offsetY = baseOffsetY + canvasState.panOffset.y;

    return {
      offsetX,
      offsetY,
      displayWidth,
      displayHeight,
      scale: currentScale
    };
  }, [canvasState.zoom, canvasState.panOffset]);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // --- Part 1: Your High-Quality Setup (UNCHANGED) ---
    // This section is taken directly from your upgraded component. It ensures
    // the canvas is cleared correctly and the image is drawn with anti-aliasing.
    const canvasDisplayWidth = canvas.width / (window.devicePixelRatio || 1);
    const canvasDisplayHeight = canvas.height / (window.devicePixelRatio || 1);

    ctx.clearRect(0, 0, canvasDisplayWidth, canvasDisplayHeight);
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(0, 0, canvasDisplayWidth, canvasDisplayHeight);

    const transform = calculateImageTransform();
    transformRef.current = transform;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(img, transform.offsetX, transform.offsetY, transform.displayWidth, transform.displayHeight);

    // --- Part 2: My Interactivity Addition (NEW) ---
    // This logic is inserted here. It runs AFTER the main image is drawn,
    // so the bounding boxes appear ON TOP of the document image.
    if (tableData?.grid) {
      tableData.grid.forEach(row => {
        row.forEach(cell => {
          if (cell && !('spanned' in cell) && cell.geometry) {
            const { BoundingBox } = cell.geometry;
            const isSelected = cell.id === selectedBlockId;

            // Calculate box position, accounting for current zoom and pan via the 'transform' object
            const rectX = BoundingBox.Left * transform.displayWidth + transform.offsetX;
            const rectY = BoundingBox.Top * transform.displayHeight + transform.offsetY;
            const rectWidth = BoundingBox.Width * transform.displayWidth;
            const rectHeight = BoundingBox.Height * transform.displayHeight;
            
            // Set styles for the box (e.g., thicker and different color if selected)
            ctx.lineWidth = isSelected ? 3 : 1.5;
            ctx.strokeStyle = isSelected ? '#1976d2' : '#ef5350';
            ctx.fillStyle = isSelected ? 'rgba(25, 118, 210, 0.3)' : 'rgba(239, 83, 80, 0.15)';
            
            // Draw the box
            ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
            ctx.strokeRect(rectX, rectY, rectWidth, rectHeight);
          }
        });
      });
    }

    // --- Part 3: Your UI Overlays (UNCHANGED) ---
    // This final section from your code draws temporary UI feedback, like the
    // annotation selection rectangle and the panning indicator, on top of everything else.
    if (currentSelection && canvasState.isAnnotating) {
      ctx.strokeStyle = '#2196f3';
      ctx.lineWidth = 2;
      ctx.fillStyle = 'rgba(33, 150, 243, 0.15)';
      ctx.fillRect(
        currentSelection.x,
        currentSelection.y,
        currentSelection.width,
        currentSelection.height
      );
      ctx.strokeRect(
        currentSelection.x,
        currentSelection.y,
        currentSelection.width,
        currentSelection.height
      );
    }

    if (canvasState.isPanning && !canvasState.isAnnotating) {
      ctx.strokeStyle = '#ff9800';
      ctx.lineWidth = 3;
      ctx.strokeRect(2, 2, canvasDisplayWidth - 4, canvasDisplayHeight - 4);
    }
  }, [
    // This dependency array combines the dependencies from both sets of logic
    canvasState.zoom, 
    canvasState.panOffset, 
    canvasState.isAnnotating, 
    canvasState.isPanning, 
    currentSelection, 
    calculateImageTransform,
    tableData,          // Added for interactivity
    selectedBlockId     // Added for interactivity
  ]);

  // Redraw when state changes
  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  const getCanvasMousePos = useCallback((e: MouseEvent): { x: number; y: number } => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  }, []);

  const isPointInImage = useCallback((x: number, y: number): boolean => {
    const transform = transformRef.current;
    return x >= transform.offsetX && 
           x <= transform.offsetX + transform.displayWidth &&
           y >= transform.offsetY && 
           y <= transform.offsetY + transform.displayHeight;
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!isMainCanvas) return;
    
    const pos = getCanvasMousePos(e.nativeEvent);
    
    if (canvasState.isPanning) {
      // Start panning
      setCanvasState(prev => ({
        ...prev,
        isDragging: true,
        startPoint: pos
      }));
    } else if (canvasState.isAnnotating) {
      // Start annotation if clicking within image bounds
      if (!isPointInImage(pos.x, pos.y)) return;
      
      setCanvasState(prev => ({
        ...prev,
        isDrawing: true,
        startPoint: pos
      }));
      
      setCurrentSelection({
        x: pos.x,
        y: pos.y,
        width: 0,
        height: 0
      });
    }
  }, [canvasState.isAnnotating, canvasState.isPanning, isMainCanvas, getCanvasMousePos, isPointInImage]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const pos = getCanvasMousePos(e.nativeEvent);
    
    if (canvasState.isDragging && canvasState.startPoint && canvasState.isPanning) {
      // Handle panning
      const deltaX = pos.x - canvasState.startPoint.x;
      const deltaY = pos.y - canvasState.startPoint.y;
      
      setCanvasState(prev => ({
        ...prev,
        panOffset: {
          x: prev.panOffset.x + deltaX,
          y: prev.panOffset.y + deltaY
        },
        startPoint: pos
      }));
    } else if (canvasState.isDrawing && canvasState.startPoint && canvasState.isAnnotating) {
      // Handle annotation selection
      const transform = transformRef.current;
      
      // Clamp coordinates to image bounds
      const clampedX = Math.max(transform.offsetX, Math.min(pos.x, transform.offsetX + transform.displayWidth));
      const clampedY = Math.max(transform.offsetY, Math.min(pos.y, transform.offsetY + transform.displayHeight));
      
      const width = clampedX - canvasState.startPoint.x;
      const height = clampedY - canvasState.startPoint.y;
      
      setCurrentSelection({
        x: width < 0 ? clampedX : canvasState.startPoint.x,
        y: height < 0 ? clampedY : canvasState.startPoint.y,
        width: Math.abs(width),
        height: Math.abs(height)
      });
    }
  }, [canvasState.isDragging, canvasState.isDrawing, canvasState.startPoint, canvasState.isPanning, canvasState.isAnnotating, getCanvasMousePos]);

  const handleMouseUp = useCallback(() => {
    if (canvasState.isDragging) {
      // End panning
      setCanvasState(prev => ({
        ...prev,
        isDragging: false,
        startPoint: null
      }));
    } else if (canvasState.isDrawing && currentSelection) {
      // End annotation
      setCanvasState(prev => ({
        ...prev,
        isDrawing: false,
        startPoint: null,
        isAnnotating: false
      }));
      
      // Only create annotation if selection is large enough
      if (currentSelection.width > 20 && currentSelection.height > 20) {
        createAnnotation();
      }
      
      setCurrentSelection(null);
    }
  }, [canvasState.isDragging, canvasState.isDrawing, currentSelection]);

  const createAnnotation = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img || !currentSelection) return;

    const transform = transformRef.current;
    
    // Convert canvas coordinates to image coordinates
    const imageX = (currentSelection.x - transform.offsetX) / transform.scale;
    const imageY = (currentSelection.y - transform.offsetY) / transform.scale;
    const imageWidth = currentSelection.width / transform.scale;
    const imageHeight = currentSelection.height / transform.scale;
    
    // Clamp to image bounds
    const clampedX = Math.max(0, Math.min(imageX, img.width));
    const clampedY = Math.max(0, Math.min(imageY, img.height));
    const clampedWidth = Math.min(imageWidth, img.width - clampedX);
    const clampedHeight = Math.min(imageHeight, img.height - clampedY);
    
    // Ensure valid selection
    if (clampedWidth > 0 && clampedHeight > 0) {
      // Create high-quality cropped canvas
      const croppedCanvas = document.createElement('canvas');
      const croppedCtx = croppedCanvas.getContext('2d');
      if (croppedCtx) {
        croppedCanvas.width = clampedWidth;
        croppedCanvas.height = clampedHeight;
        
        // Enable high-quality rendering for cropped canvas
        croppedCtx.imageSmoothingEnabled = true;
        croppedCtx.imageSmoothingQuality = 'high';
        
        // Draw cropped portion at full resolution
        croppedCtx.drawImage(
          img,
          clampedX,
          clampedY,
          clampedWidth,
          clampedHeight,
          0,
          0,
          clampedWidth,
          clampedHeight
        );
        
        // Create annotation
        const annotation: AnnotatedSection = {
          id: `annotation-${Date.now()}`,
          x: clampedX,
          y: clampedY,
          width: clampedWidth,
          height: clampedHeight,
          imageData: croppedCanvas.toDataURL('image/jpeg', 0.95) // Higher quality JPEG
        };
        
        onAnnotationCreate?.(annotation);
      }
    }
  }, [currentSelection, onAnnotationCreate]);

  const handleZoomIn = useCallback(() => {
    setCanvasState(prev => ({ ...prev, zoom: Math.min(prev.zoom + 0.3, 5) }));
  }, []);

  const handleZoomOut = useCallback(() => {
    setCanvasState(prev => ({ ...prev, zoom: Math.max(prev.zoom - 0.3, 0.3) }));
  }, []);

  const handleResetZoom = useCallback(() => {
    setCanvasState(prev => ({ 
      ...prev, 
      zoom: 1, 
      panOffset: { x: 0, y: 0 } 
    }));
  }, []);

  const handleAnnotate = useCallback(() => {
    setCanvasState(prev => ({
      ...prev,
      isAnnotating: !prev.isAnnotating,
      isPanning: false, // Disable panning when annotating
      isDrawing: false,
      startPoint: null
    }));
    setCurrentSelection(null);
  }, []);

  const handlePan = useCallback(() => {
    setCanvasState(prev => ({
      ...prev,
      isPanning: !prev.isPanning,
      isAnnotating: false, // Disable annotation when panning
      isDrawing: false,
      startPoint: null
    }));
    setCurrentSelection(null);
  }, []);

  const getCursorStyle = () => {
    if (canvasState.isPanning && isMainCanvas) return 'grab';
    if (canvasState.isDragging && isMainCanvas) return 'grabbing';
    if (canvasState.isAnnotating && isMainCanvas) return 'crosshair';
    return 'default';
  };

  return (
    <Paper elevation={0} sx={{ mb: 2, boxSizing: 'border-box' }}>
      {isMainCanvas && (
        <Toolbar
          sx={{
            p: { xs: 1, sm: 1 },
            minHeight: 'auto !important',
            mb: 2,
            borderRadius: 2,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Visibility sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                letterSpacing: 0.5,
                display: { xs: 'none', sm: 'none', md: 'block' }
              }}
            >
              Document Preview
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {annotationIds.length > 0 && (
            <>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={isAllCoverageOn}
                  onChange={(e) => toggleAllCoverage(e.target.checked, annotationIds)}
                />
              }
              label={<Typography variant="body2">Show All Coverage</Typography>}
              sx={{ mr: 2 }}
            />
            <Divider orientation="vertical" flexItem sx={{ mx: 0 }} />
            </>
          )}
            <Tooltip title="Zoom Out" arrow>
            <span>
              <IconButton onClick={handleZoomOut} size="small">
                <ZoomOut fontSize="small" />
              </IconButton>
            </span>
            </Tooltip>

            <Tooltip title="Reset View" arrow>
            <span>
              <IconButton onClick={handleResetZoom} size="small">
                <Refresh fontSize="small" />
              </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Zoom In" arrow>
              <span>
              <IconButton onClick={handleZoomIn} size="small">
                <ZoomIn fontSize="small" />
              </IconButton>
              </span>
            </Tooltip>
          </Box>
          
          <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
          
          <Tooltip title={canvasState.isPanning ? 'Exit Pan Mode' : 'Pan & Drag'} arrow>
          <span>
            <Button
              onClick={handlePan}
              variant={canvasState.isPanning ? 'contained' : 'outlined'}
              size="small"
              color={canvasState.isPanning ? 'warning' : 'primary'}
              startIcon={<PanTool fontSize="small" />}
              sx={{ mr: 1 }}
            >
              Pan
            </Button>
            </span>
          </Tooltip>
          
          <Tooltip
            title={canvasState.isAnnotating ? 'Exit Annotation Mode' : 'Start Annotating'}
            arrow
          >
            <span>
            <Button
              onClick={handleAnnotate}
              variant="contained"
              size="small"
              color={canvasState.isAnnotating ? 'error' : 'primary'}
              startIcon={<Edit fontSize="small" />}
            >
              {canvasState.isAnnotating ? 'Stop' : 'Annotate'}
            </Button>
            </span>
          </Tooltip>
        </Toolbar>
      )}
      
      <Box
        ref={containerRef}
        sx={{
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          minHeight: isMainCanvas ? 400 : 250,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {!isMainCanvas && (
          <Toolbar sx={{ p: 2, minHeight: 'auto !important' }}>
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
              {title || 'Annotated Section'}
            </Typography>
            {onDelete && (
              <Tooltip title="Delete">
                <IconButton onClick={onDelete} size="small" color="error">
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Toolbar>
        )}
        
        <canvas
          ref={canvasRef}
          style={{
            cursor: getCursorStyle(),
            display: 'block',
            flex: 1
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp} // Handle mouse leaving canvas
        />
      </Box>
      
      {canvasState.isAnnotating && isMainCanvas && (
        <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
          Click and drag to select an area for annotation
        </Typography>
      )}
      
      {canvasState.isPanning && isMainCanvas && (
        <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
          Click and drag to pan around the image
        </Typography>
      )}
    </Paper>
  );
};